import { useInfiniteQuery } from "@tanstack/react-query";
import { useLocale } from "next-intl";
import { fetchClient } from "@/lib/api-client";
import type { BlogQueryParams } from "@/lib/blog/filter";
import { BLOG_SITE } from "@/lib/blog/site";

interface Params extends BlogQueryParams {
  limit?: number;
  enabled?: boolean;
  startPage?: number;
}

export const useFetchBlogPosts = (params: Params = {}) => {
  const {
    category,
    status,
    limit = 10,
    enabled = true,
    startPage = 1,
  } = params;
  const locale = useLocale();

  return useInfiniteQuery({
    queryKey: [
      "blog-posts",
      BLOG_SITE,
      category,
      status,
      limit,
      startPage,
      locale,
    ],
    queryFn: async ({ pageParam = startPage }) => {
      const response = await fetchClient.GET("/blog", {
        params: {
          query: {
            site: BLOG_SITE,
            page: typeof pageParam === "number" ? pageParam : startPage,
            limit,
            category: category || undefined,
            status: status || undefined,
            locale: locale as "fa" | "en" | "ar",
          },
        },
      });
      const err = (response as { error?: unknown }).error;
      if (err) throw err;
      return response.data;
    },
    initialPageParam: startPage,
    getNextPageParam: (lastPage) => {
      const p = lastPage?.data?.pagination as
        | { hasNextPage?: boolean; page?: number }
        | undefined;
      return p?.hasNextPage ? (p.page ?? 1) + 1 : undefined;
    },
    enabled,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
